import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { Topbar, Sidebar, Loader } from '../../../components';
import useConfigOptions from '../../../hooks/useConfigOptions';
import { HugeiconsIcon } from '@hugeicons/react';
import { PencilEdit02Icon, Search01Icon, Alert02Icon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';

// Admin > Edit Loan
// Correct the pricing inputs of a Pension loan before disbursement. Only the
// inputs are editable; the backend re-runs the application pricing engine and
// every dependent figure (rate, amount, premium, fees, commission, dates) is
// recalculated from them.

const API = '/loans/staff/admin/loan-edit';

const LABELS = {
    loanKind: 'Loan type',
    pensionState: 'Secured / unsecured',
    amountReq: 'Amount requested',
    paymentDuration: 'Duration (months)',
    paymentPlan: 'Payment plan',
    startDate: 'First payment date',
    pensionAsset: 'Pension asset',
    collateralType: 'Collateral type',
    incomeAmt: 'Monthly income',
    debtAmt: 'Monthly debt',
    score: 'Credit score',
    monthInterestRate: 'Monthly interest rate (%)',
    amount: 'Loan amount',
    amountElig: 'Eligible amount',
    DIR: 'Debt-to-income (%)',
    willingness: 'Willingness to repay',
    capacity: 'Capacity',
    creditConc: 'Credit conclusion',
    endDate: 'Last payment date',
    paymentDates: 'Payment dates',
    standingOrderDates: 'Standing order dates',
    'accountInfo.amountGiven': 'Amount given to client',
    'accountInfo.premium': 'Monthly premium',
    'accountInfo.interestRate': 'Interest rate on account (%)',
    'accountInfo.numPayments': 'Number of payments',
    'accountInfo.totalPayment': 'Total repayment',
    'accountInfo.lastPayment': 'Last payment',
    'accountInfo.sumInterest': 'Total interest',
    'accountInfo.processingFee': 'Processing fee',
    'accountInfo.insuranceFee': 'Insurance fee',
    'accountInfo.faciLetterProcessingFee': 'Facility letter processing fee (%)',
    'accountInfo.faciLetterInsuranceFee': 'Facility letter insurance fee (%)',
    'accountInfo.petraCommTotal': 'Petra commission (total)',
    'accountInfo.petraCommPM': 'Petra commission per month',
    'accountInfo.petraInterestPM': 'Petra interest per month',
    'accountInfo.axisProcessingFee': 'Axis processing fee',
    'accountInfo.metroProcessingFee': 'Metropolitan processing fee',
    'accountInfo.oldMutualProcessingFee': 'Old Mutual processing fee',
    'accountInfo.kimptonProcessingFee': 'Kimpton processing fee',
    'accountInfo.firstMeritProcessingFee': 'First Merit processing fee',
    'accountInfo.cedisPayProcessingFee': 'CedisPay processing fee',
};
const label = (f) => LABELS[f] || f.replace('accountInfo.', '').replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') return v.toLocaleString('en-GB', { maximumFractionDigits: 2 });
    return String(v);
};

// Figures shown in the "current figures" card.
const SUMMARY_FIGURES = [
    ['score', (f) => f.score],
    ['monthInterestRate', (f) => f.monthInterestRate],
    ['amount', (f) => f.amount],
    ['accountInfo.premium', (f) => f.accountInfo?.premium],
    ['accountInfo.numPayments', (f) => f.accountInfo?.numPayments],
    ['accountInfo.totalPayment', (f) => f.accountInfo?.totalPayment],
    ['accountInfo.processingFee', (f) => f.accountInfo?.processingFee],
    ['accountInfo.insuranceFee', (f) => f.accountInfo?.insuranceFee],
    ['accountInfo.petraCommTotal', (f) => f.accountInfo?.petraCommTotal],
    ['accountInfo.amountGiven', (f) => f.accountInfo?.amountGiven],
    ['endDate', (f) => f.endDate],
];

const inputCls = (changed, disabled) =>
    `w-full text-sm rounded-lg px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-900 dark:text-gray-100 ${
        changed ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-500/10' : 'border-gray-200 dark:border-gray-700'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`;

function Field({ name, changed, children }) {
    return (
        <label className="block">
            <span className="flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {label(name)}
                {changed && <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Changed</span>}
            </span>
            {children}
        </label>
    );
}

function Card({ title, hint, children }) {
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-5">
            {(title || hint) && (
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
                    {hint && <span className="text-xs text-gray-400">{hint}</span>}
                </div>
            )}
            {children}
        </div>
    );
}

function Warnings({ items }) {
    if (!items?.length) return null;
    return (
        <div className="space-y-2">
            {items.map((w, i) => (
                <div key={i} className="flex items-start gap-2 px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-300">
                    <HugeiconsIcon icon={Alert02Icon} size={16} className="mt-0.5 shrink-0" />
                    <span>{w}</span>
                </div>
            ))}
        </div>
    );
}

function ChangesTable({ rows }) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                        <th className="py-2 pr-4 font-medium">Field</th>
                        <th className="py-2 pr-4 font-medium">Current</th>
                        <th className="py-2 font-medium">New</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {rows.map((r) => (
                        <tr key={r.group + r.field}>
                            <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">{label(r.field)}</td>
                            <td className="py-2 pr-4 text-gray-400 line-through decoration-gray-300 tabular-nums">{fmt(r.before)}</td>
                            <td className="py-2 font-semibold text-gray-900 dark:text-gray-50 tabular-nums">{fmt(r.after)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function LoanEdit() {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pensionLoanType, pensionStateOpts, paymentPlanOpts, paymentDurationOpts, pensionCollateralOpts } = useConfigOptions();

    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);       // loaded loan
    const [form, setForm] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [reason, setReason] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [applying, setApplying] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const toast = (type, message) => dispatch(show({ type, state: true, message }));
    const expired = (res) => {
        if (res?.code !== 401) return false;
        sessionStorage.clear();
        navigate('/login');
        toast('error', 'Your session has expired. Please login to continue.');
        return true;
    };

    const changed = data
        ? Object.keys(form).filter((k) => String(form[k] ?? '') !== String(data.inputs[k] ?? ''))
        : [];

    const resetFrom = (loaded) => {
        setData(loaded);
        setForm(loaded.inputs);
        setPreview(null);
        setReason('');
        setConfirming(false);
    };

    const load = async (e) => {
        e?.preventDefault();
        if (!query.trim()) return;
        setLoading(true);
        setLastResult(null);
        const res = await fetchRequests(API, 'GET', authStaff(accessToken), { id: query.trim() }, {});
        setLoading(false);
        if (expired(res)) return;
        if (!res || res.err) {
            setData(null);
            toast('error', res?.err || 'Could not load loan');
            return;
        }
        resetFrom(res);
    };

    const update = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setPreview(null);
        setConfirming(false);
    };

    const changesPayload = () => Object.fromEntries(changed.map((k) => [k, form[k]]));

    const runPreview = async () => {
        setPreviewing(true);
        const res = await fetchRequests(API, 'POST', authStaff(accessToken), { mode: 'preview' }, { id: data.loan._id, changes: changesPayload() });
        setPreviewing(false);
        if (expired(res)) return;
        if (!res || res.err) {
            setPreview(null);
            toast('error', res?.err || 'Could not preview changes');
            return;
        }
        setPreview(res);
    };

    const apply = async () => {
        setApplying(true);
        const res = await fetchRequests(API, 'POST', authStaff(accessToken), {}, { id: data.loan._id, changes: changesPayload(), reason });
        setApplying(false);
        setConfirming(false);
        if (expired(res)) return;
        if (!res || res.err) {
            toast('error', res?.err || 'Could not update loan');
            return;
        }
        toast('success', res.message || 'Loan updated');
        setLastResult(res);
        // reload so the saved values become the new "current"
        const fresh = await fetchRequests(API, 'GET', authStaff(accessToken), { id: data.loan._id }, {});
        if (fresh && !fresh.err) resetFrom(fresh);
    };

    const disabled = !data?.editable;

    const select = (name, opts) => (
        <Field name={name} changed={changed.includes(name)}>
            <select className={inputCls(changed.includes(name), disabled)} value={form[name] ?? ''} disabled={disabled} onChange={(e) => update(name, e.target.value)}>
                {/* keep a saved value visible even if it is no longer an option */}
                {data && !(opts || []).some((o) => String(o.value) === String(data.inputs[name])) && (
                    <option value={data.inputs[name] ?? ''}>{data.inputs[name] || '—'}</option>
                )}
                {(opts || []).map((o) => <option key={o.value} value={o.value}>{o.text || o.value}</option>)}
            </select>
        </Field>
    );

    const number = (name) => (
        <Field name={name} changed={changed.includes(name)}>
            <input type="number" min="0" step="0.01" className={inputCls(changed.includes(name), disabled)} value={form[name] ?? ''} disabled={disabled}
                onChange={(e) => update(name, e.target.value === '' ? '' : Number(e.target.value))} />
        </Field>
    );

    const inputRows = preview?.changes?.filter((r) => r.group === 'input') || [];
    const figureRows = preview?.changes?.filter((r) => r.group === 'figure') || [];

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />
            <main className="main-content">
                <section className="section-container space-y-5">

                    {/* Header */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1e3a6e]/10 text-[#1e3a6e] dark:text-blue-400 flex items-center justify-center">
                            <HugeiconsIcon icon={PencilEdit02Icon} size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Edit Loan</h1>
                            <p className="text-sm text-gray-400">Correct a Pension loan's pricing inputs before disbursement. Every dependent figure is recalculated.</p>
                        </div>
                    </div>

                    {/* Find loan */}
                    <form onSubmit={load} className="flex gap-2 max-w-xl">
                        <div className="relative flex-1">
                            <HugeiconsIcon icon={Search01Icon} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Loan ID, e.g. pen-6ac8…"
                                className="w-full text-sm rounded-lg pl-9 pr-3 py-2.5 border border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                        </div>
                        <button type="submit" disabled={loading || !query.trim()}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] disabled:opacity-50">
                            {loading && <Loader color="text-white" size="w-4 h-4" />}
                            Load loan
                        </button>
                    </form>

                    {lastResult && (
                        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-300">
                            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="mt-0.5 shrink-0" />
                            <span>
                                Loan updated ({lastResult.changes?.length} fields). Documents: {lastResult.documents}. Client record: {lastResult.clientRecord}.
                            </span>
                        </div>
                    )}

                    {data && (
                        <>
                            {/* Loan summary */}
                            <div className="flex flex-wrap gap-x-8 gap-y-2 px-5 py-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm text-sm">
                                {[['Loan ID', data.loan.loanId], ['Phone', data.loan.phone], ['Status', `${data.loan.state} / ${data.loan.adminStage}`], ['Relationship manager', data.loan.relManager]].map(([k, v]) => (
                                    <div key={k}>
                                        <p className="text-xs text-gray-400">{k}</p>
                                        <p className="font-medium text-gray-900 dark:text-gray-100">{v || '—'}</p>
                                    </div>
                                ))}
                            </div>

                            {!data.editable && <Warnings items={[data.notEditableReason]} />}
                            <Warnings items={preview ? preview.warnings : data.warnings} />

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                                {/* Editable inputs */}
                                <div className="lg:col-span-2">
                                    <Card title="Loan inputs" hint={changed.length ? `${changed.length} changed` : 'Change any value, then preview'}>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {select('loanKind', pensionLoanType)}
                                            {select('pensionState', pensionStateOpts)}
                                            {number('amountReq')}
                                            {select('paymentPlan', paymentPlanOpts)}
                                            {select('paymentDuration', paymentDurationOpts)}
                                            <Field name="startDate" changed={changed.includes('startDate')}>
                                                <input type="date" className={inputCls(changed.includes('startDate'), disabled)} value={form.startDate ?? ''} disabled={disabled}
                                                    onChange={(e) => update('startDate', e.target.value)} />
                                            </Field>
                                            {number('pensionAsset')}
                                            {select('collateralType', pensionCollateralOpts)}
                                            {number('incomeAmt')}
                                            {number('debtAmt')}
                                        </div>
                                        <div className="flex items-center justify-end gap-3 mt-5">
                                            {changed.length > 0 && (
                                                <button type="button" onClick={() => resetFrom(data)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
                                                    Discard changes
                                                </button>
                                            )}
                                            <button type="button" onClick={runPreview} disabled={disabled || !changed.length || previewing}
                                                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] disabled:opacity-50">
                                                {previewing && <Loader color="text-white" size="w-4 h-4" />}
                                                Preview changes
                                            </button>
                                        </div>
                                    </Card>
                                </div>

                                {/* Current figures (read-only) */}
                                <Card title="Current figures" hint="calculated">
                                    <dl className="space-y-2 text-sm">
                                        {SUMMARY_FIGURES.map(([name, get]) => (
                                            <div key={name} className="flex justify-between gap-3">
                                                <dt className="text-gray-500 dark:text-gray-400">{label(name)}</dt>
                                                <dd className="font-medium text-gray-900 dark:text-gray-100 tabular-nums">{fmt(get(data.figures))}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </Card>
                            </div>

                            {/* Preview */}
                            {preview && (
                                <Card title="Preview" hint={`${preview.changes.length} fields will change`}>
                                    <div className="space-y-6">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Inputs</p>
                                            <ChangesTable rows={inputRows} />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Recalculated figures</p>
                                            {figureRows.length ? <ChangesTable rows={figureRows} /> : <p className="text-sm text-gray-400">No figures change.</p>}
                                        </div>

                                        <div className="border-t border-gray-100 dark:border-gray-700 pt-5 space-y-3">
                                            <label className="block">
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Reason for this change (saved to the audit log)</span>
                                                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Staff selected Normal Pension; client is an Axis customer"
                                                    className="mt-1 w-full text-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                                            </label>

                                            {!confirming ? (
                                                <div className="flex justify-end">
                                                    <button type="button" onClick={() => setConfirming(true)} disabled={reason.trim().length < 5}
                                                        className="px-5 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                                                        Apply changes
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap items-center justify-end gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20">
                                                    <span className="text-sm text-amber-800 dark:text-amber-300 mr-auto">
                                                        This updates the loan, regenerates its facility letter and reports, and updates the client record. Continue?
                                                    </span>
                                                    <button type="button" onClick={() => setConfirming(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Cancel</button>
                                                    <button type="button" onClick={apply} disabled={applying}
                                                        className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                                                        {applying && <Loader color="text-white" size="w-4 h-4" />}
                                                        Confirm and apply
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {/* Edit history */}
                            {data.edits?.length > 0 && (
                                <Card title="Edit history" hint="most recent first">
                                    <ul className="divide-y divide-gray-50 dark:divide-gray-700/50 text-sm">
                                        {data.edits.map((e) => (
                                            <li key={e._id} className="py-3 flex flex-wrap gap-x-4 gap-y-1">
                                                <span className="text-gray-400 tabular-nums">{new Date(e.createdAt).toLocaleString('en-GB')}</span>
                                                <span className="font-medium text-gray-800 dark:text-gray-100">{e.editedBy?.name || e.editedBy?.email}</span>
                                                <span className="text-gray-500 dark:text-gray-400 flex-1 min-w-[12rem]">{e.reason}</span>
                                                <span className="text-gray-400">{e.changes?.length} fields</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            )}
                        </>
                    )}
                </section>
            </main>
        </div>
    );
}

export default LoanEdit;
